import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
  Alert,
  IconButton
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Cigar, UpdateCigarRequest, useUpdateCigarMutation, useDeleteCigarMutation } from '@store/api/cigarApi'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { offlineDeleteCigar, offlineUpdateCigar } from '@services/offlineWriteService'

interface RemoveCigarDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  cigar: Cigar | null
}

const removalReasons = [
  { value: 'smoked', label: 'Smoked', description: 'Cigar was smoked and enjoyed' },
  { value: 'traded', label: 'Traded', description: 'Cigar was traded with another collector' },
  { value: 'gifted', label: 'Gifted', description: 'Cigar was given as a gift' },
  { value: 'damaged', label: 'Damaged / Disposed', description: 'Cigar was damaged or disposed of' },
  { value: 'delete', label: 'Delete Permanently', description: 'Remove from collection entirely' },
]

const RemoveCigarDialog: React.FC<RemoveCigarDialogProps> = ({ open, onClose, onSuccess, cigar }) => {
  const dispatch = useAppDispatch()
  const { isOnline } = useAppSelector((state) => state.offline)
  const [reason, setReason] = useState('smoked')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  const [updateCigar, { isLoading: isUpdating }] = useUpdateCigarMutation()
  const [deleteCigar, { isLoading: isDeleting }] = useDeleteCigarMutation()

  const isLoading = isUpdating || isDeleting

  const handleClose = () => {
    setReason('smoked')
    setNotes('')
    setError('')
    onClose()
  }

  const handleConfirm = async () => {
    if (!cigar) return
    setError('')

    try {
      if (!isOnline) {
        if (reason === 'delete') {
          await offlineDeleteCigar(dispatch, cigar.id)
        } else {
          const updates: UpdateCigarRequest = { status: reason as UpdateCigarRequest['status'] }
          if (notes.trim()) {
            updates.notes = cigar.notes
              ? `${cigar.notes}\n[Removed: ${reason}] ${notes.trim()}`
              : `[Removed: ${reason}] ${notes.trim()}`
          }
          const merged: Cigar = { ...cigar, ...updates, updatedAt: new Date().toISOString() }
          await offlineUpdateCigar(dispatch, cigar.id, { ...updates }, merged)
        }
        handleClose()
        onSuccess?.()
        return
      }

      if (reason === 'delete') {
        await deleteCigar(cigar.id).unwrap()
      } else {
        const updates: UpdateCigarRequest = { status: reason as UpdateCigarRequest['status'] }
        if (notes.trim()) {
          updates.notes = cigar.notes
            ? `${cigar.notes}\n[Removed: ${reason}] ${notes.trim()}`
            : `[Removed: ${reason}] ${notes.trim()}`
        }
        await updateCigar({ id: cigar.id, updates }).unwrap()
      }
      handleClose()
      onSuccess?.()
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to remove cigar. Please try again.')
    }
  }

  if (!cigar) return null

  const cigarName = `${cigar.brand?.name || 'Unknown'} - ${cigar.cigarLine?.name || 'Unknown'}`

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Remove Cigar</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          <strong>{cigarName}</strong>
        </Typography>
        {cigar.vitola && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {cigar.vitola.name} &bull; Qty: {cigar.quantity}
          </Typography>
        )}

        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Reason for removal:
          </Typography>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {removalReasons.map((r) => (
              <FormControlLabel
                key={r.value}
                value={r.value}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">{r.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 0.5 }}
              />
            ))}
          </RadioGroup>
        </Box>

        <TextField
          label="Additional notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 255 }}
        />

        {reason === 'delete' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will permanently delete the cigar from your collection. This action cannot be undone.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={reason === 'delete' ? 'error' : 'primary'}
          disabled={isLoading}
        >
          {isLoading ? 'Removing...' : reason === 'delete' ? 'Delete Permanently' : 'Remove Cigar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RemoveCigarDialog
